import { AppContext } from "01Data/AppData";
import { Device } from "01Data/DataTypes";
import PageTemplate from "03AdminPages/00Templates/PageTemplate";
import { useModel } from "99Tools/useModel";
import { ComStatusExpression } from "99Tools/utils";
import { Article, OpenInNew } from "@mui/icons-material";
import { Backdrop, Button, CircularProgress, styled } from "@mui/material";
import Paper from "@mui/material/Paper";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TablePagination from "@mui/material/TablePagination";
import TableRow from "@mui/material/TableRow";
import { useContext, useEffect, useState } from "react";
import { useMediaQuery } from "react-responsive";
import { useNavigate } from "react-router";
import { LIST_HEADER_COLOR } from "Settings";

const ColorHeader = styled(TableCell)(({ theme }) => ({
  backgroundColor: LIST_HEADER_COLOR,
  color: theme.palette.common.white,
}));

/**
 * カラム設定
 */
const columns = [
  {
    id: "id",
    label: "デバイスID",
    minWidthPC: 150,
    minWidthSmartphone: "18vw",
  },
  {
    id: "kind",
    label: "種別",
    minWidthPC: 50,
    minWidthSmartphone: "8vw",
  },
  { id: "state", label: "通信状態", minWidthPC: 50, minWidthSmartphone: "8vw" },
  {
    id: "date",
    label: "最終到達時間",
    minWidthPC: 150,
    minWidthSmartphone: "18vw",
  },
  {
    id: "user",
    label: "管理者",
    minWidthPC: 150,
    minWidthSmartphone: "18vw",
  },
  { id: "map", label: "詳細場所", minWidthPC: 50, minWidthSmartphone: "8vw" },
  { id: "showLog", label: "", minWidthPC: 50, minWidthSmartphone: "8vw" },
];

/**
 * 漏水一覧
 * @returns TSX
 */
const UserBody = () => {
  const a = useContext(AppContext).dataAdmin;
  const list = a.devices;
  const navigation = useNavigate();

  const handleOpenLog = (i: number) => {
    a.log.Clear();
    const last =
      list.devices[i].StatusUpdatedAt === "-"
        ? ""
        : new Date(list.devices[i].StatusUpdatedAt).toISOString().split("T")[0];
    navigation(`/admin/log/id?id=${list.devices[i].id}&last=${last}`);
    list.changed();
  };

  const handleShowMap = (url: string) => {
    window.open(url, "_blank");
  };

  const rows = list.devices
    .sort((a: Device, b: Device) => {
      if (a.user.UserId === b.user.UserId) {
        return a.id.localeCompare(b.id);
      } else {
        return a.user.UserName.localeCompare(b.user.UserName);
      }
    })
    .slice(
      list.pageID * list.rowsPerPageID,
      list.pageID * list.rowsPerPageID + list.rowsPerPageID
    )
    .map((row: Device, i: number) => {
      const index = list.pageID * list.rowsPerPageID + i;
      return (
        <TableRow
          hover
          role="checkbox"
          tabIndex={-1}
          key={row.StatusUpdatedAt + row.id}
        >
          <TableCell key={columns[0].id + i} align="center">
            {row.id}
          </TableCell>
          <TableCell key={columns[1].id + i} align="center">
            {row.kind}
          </TableCell>
          <TableCell key={columns[2].id + i} align="center">
            {ComStatusExpression(row.ComStatus)}
          </TableCell>
          <TableCell key={columns[3].id + i} align="center">
            {row.StatusUpdatedAt}
          </TableCell>
          <TableCell key={columns[4].id + i} align="center">
            {row.user.UserName}
            <br />({row.user.Email})
          </TableCell>
          <TableCell key={columns[5].id + i} align="center">
            {row.ImageURL === "" ? (
              <Button
                size="small"
                variant="contained"
                endIcon={<OpenInNew />}
                disabled
                sx={{ textWrap: "nowrap" }}
              >
                表示
              </Button>
            ) : (
              <Button
                size="small"
                variant="contained"
                endIcon={<OpenInNew />}
                onClick={() => handleShowMap(row.ImageURL)}
                sx={{ textWrap: "nowrap" }}
              >
                表示
              </Button>
            )}
          </TableCell>
          <TableCell key={columns[6].id + i} align="center">
            <Button
              size="small"
              startIcon={<Article />}
              variant="contained"
              onClick={() => handleOpenLog(index)}
            >
              ログ表示
            </Button>
          </TableCell>
        </TableRow>
      );
    });

  return <TableBody>{rows}</TableBody>;
};

/**
 * ユーザテーブル
 * @returns TSX
 */
const UserTable = () => {
  const list = useContext(AppContext).dataAdmin.devices;
  useModel(list);
  const isSmartphone = useMediaQuery({
    query: "(max-width: 600px)",
  });

  const handleChangePage = (event: unknown, newPage: number) => {
    list.pageID = newPage;
    list.changed();
  };

  const handleChangeRowsPerPage = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    list.rowsPerPageID = +event.target.value;
    list.pageID = 0;
    list.changed();
  };

  const header = columns.map((column) => (
    <ColorHeader
      key={column.id}
      align="center"
      style={{
        minWidth: isSmartphone ? column.minWidthSmartphone : column.minWidthPC,
      }}
    >
      {column.label}
    </ColorHeader>
  ));

  return (
    <Paper sx={{ width: "100%", overflow: "hidden" }}>
      <TableContainer sx={{ minHeight: "60vh" }}>
        <Table stickyHeader aria-label="sticky table" size="small">
          <TableHead style={{ whiteSpace: "nowrap" }}>
            <TableRow>{header}</TableRow>
          </TableHead>
          <UserBody />
        </Table>
      </TableContainer>
      <TablePagination
        rowsPerPageOptions={[10, 25, 100]}
        component="div"
        count={list.devices.length}
        rowsPerPage={list.rowsPerPageID}
        page={list.pageID}
        onPageChange={handleChangePage}
        onRowsPerPageChange={handleChangeRowsPerPage}
      />
    </Paper>
  );
};

/**
 * ID一覧表示
 * @returns TSX
 */
const IDListPage = () => {
  const d = useContext(AppContext).dataAdmin;
  const list = d.devices;
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    d.state = "autoUpdate";
    list.Update().finally(() => setLoading(false));
    return () => {
      d.state = "normal";
    };
  }, [d, list]);

  return (
    <PageTemplate title="ID一覧表示">
      {UserTable()}
      <Backdrop open={loading} sx={{ zIndex: 1201 }}>
        <CircularProgress sx={{ color: "white" }} />
      </Backdrop>
    </PageTemplate>
  );
};

export default IDListPage;
